import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'

export function buildAllergenPdf({
  venueName,
  menuName,
  dishes,
  allergenOrder,
}) {
  const doc = new jsPDF('landscape') // force wide
  const pageW = doc.internal.pageSize.getWidth()
  const pageH = doc.internal.pageSize.getHeight()
  const margin = 20
  const dateStr = new Date().toLocaleDateString('en-GB')

  // 1. VENUE NAME IN HEADER
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(16)
  doc.text(venueName || 'Restaurant', pageW / 2, 20, { align: 'center' })

  // Add menu name if provided
  if (menuName) {
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(12)
    doc.text(menuName, pageW / 2, 30, { align: 'center' })
    doc.text('Allergen Information', pageW / 2, 40, { align: 'center' })
  } else {
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(12)
    doc.text('Allergen Information', pageW / 2, 30, { align: 'center' })
  }

  // 2. TABLE WIDTH & COLUMN HEADERS - Use smaller font and better sizing
  const availableWidth = pageW - margin * 2
  const allergenNames = allergenOrder.map(capitalise)

  // Use smaller font size to ensure headers fit horizontally
  const fontSize = 8
  const cellPadding = 3

  // Calculate column widths - give more space to allergen headers, less to dish names
  const dishColWidth = availableWidth * 0.2 // 20% for dish names (reduced from 25%)
  const remainingWidth = availableWidth - dishColWidth
  const perAllergen = remainingWidth / allergenOrder.length

  // 3. SECTION HEADERS - Group dishes by section and create table with section rows
  const dishesBySection = {}
  const unassignedDishes = []

  // Group dishes by section, filtering out section header items
  dishes.forEach(dish => {
    // Skip section header items (they have type: 'section')
    if (dish.type === 'section') {
      return
    }

    const sectionName = dish.section || dish.sectionName
    if (sectionName) {
      if (!dishesBySection[sectionName]) {
        dishesBySection[sectionName] = []
      }
      dishesBySection[sectionName].push(dish)
    } else {
      unassignedDishes.push(dish)
    }
  })

  // Create table body with section headers
  const head = [['Dish Name', ...allergenNames]]
  const body = []

  // Add dishes from each section with section headers (only if section has dishes)
  Object.keys(dishesBySection).forEach(sectionName => {
    // Only add section header if there are dishes in this section
    if (dishesBySection[sectionName].length > 0) {
      // Add section header row
      const sectionRow = new Array(allergenOrder.length + 1).fill('')
      sectionRow[0] = sectionName
      body.push(sectionRow)

      // Add dishes from this section
      dishesBySection[sectionName].forEach(dish => {
        const row = [
          dish.recipeTitle || dish.name || dish.dishName || dish.title || '',
        ]
        allergenOrder.forEach(allergenKey => {
          row.push(formatAllergenForDish(dish, allergenKey))
        })
        body.push(row)
      })
    }
  })

  // Add unassigned dishes at the end
  if (unassignedDishes.length > 0) {
    unassignedDishes.forEach(dish => {
      const row = [
        dish.recipeTitle || dish.name || dish.dishName || dish.title || '',
      ]
      allergenOrder.forEach(allergenKey => {
        row.push(formatAllergenForDish(dish, allergenKey))
      })
      body.push(row)
    })
  }

  // Adjust startY based on whether menu name is present
  const startY = menuName ? 50 : 40

  autoTable(doc, {
    startY,
    head,
    body,
    theme: 'grid',
    tableWidth: 'wrap',
    margin: { left: margin, right: margin },
    styles: {
      font: 'helvetica',
      fontSize: fontSize,
      cellPadding: cellPadding,
      lineColor: [217, 217, 217], // Light grey #D9D9D9
      lineWidth: 0.2,
      valign: 'middle',
      halign: 'center',
    },
    headStyles: {
      fillColor: [134, 187, 35], // OMS green #86BB23 (avocado)
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      halign: 'center',
      valign: 'middle',
      fontSize: fontSize,
    },
    // No alternate row styles - all body rows are white
    columnStyles: {
      0: { cellWidth: dishColWidth, halign: 'left' },
      ...Object.fromEntries(
        allergenOrder.map((_, i) => [
          i + 1,
          { cellWidth: perAllergen, halign: 'center' },
        ]),
      ),
    },
    didParseCell: data => {
      const { cell, row, column, section } = data

      // Only process body rows (skip header)
      if (section === 'body') {
        const rowData = row.raw

        // Check if this is a section header row
        if (Array.isArray(rowData)) {
          const hasAllergenData = rowData.some(
            (val, index) => index > 0 && val !== '',
          )
          if (!hasAllergenData && rowData[0] && rowData[0] !== '') {
            // This is a section header row
            cell.styles.fillColor = [246, 255, 237] // Light green background
            cell.styles.fontStyle = 'bold'
            cell.styles.textColor = [0, 0, 0]
            cell.styles.halign = 'left'
            // Make it span all columns
            cell.colSpan = allergenOrder.length + 1
            return
          }
        }

        // All regular dish rows are white (no zebra striping)
        cell.styles.fillColor = [255, 255, 255] // White background

        // Handle allergen cell rendering
        if (column.index > 0) {
          // Skip dish name column
          const cellValue = cell.raw

          // Normalize and render allergen values
          const normalizedValue = normalizeAllergenValue(cellValue)

          if (normalizedValue === 'x') {
            cell.text = ['x']
            cell.styles.textColor = [46, 125, 50] // Green #2E7D32
            cell.styles.halign = 'center'
          } else if (normalizedValue === 'Traces') {
            cell.text = ['May Contain']
            cell.styles.textColor = [245, 124, 0] // Orange #F57C00
            cell.styles.halign = 'center'
          } else {
            // For "No" or empty values, show blank
            cell.text = ['']
            cell.styles.halign = 'center'
          }
        }
      }
    },
    didDrawPage: data => {
      const pageCount = doc.internal.getNumberOfPages()
      doc.setFontSize(9)
      doc.setTextColor(50)
      doc.text('Powered by Orders Made Simple', margin, pageH - 10)
      doc.text(
        `Page ${data.pageNumber} of ${pageCount}`,
        pageW / 2,
        pageH - 10,
        { align: 'center' },
      )
      doc.text(dateStr, pageW - margin, pageH - 10, { align: 'right' })
    },
  })

  return doc
}

function capitalise(s) {
  return s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, ' ')
}

// Value normalizer that maps raw values to printable content + color
function normalizeAllergenValue(value) {
  if (!value) return ''

  const normalized = String(value).toLowerCase().trim()

  // Handle various "yes" formats
  if (
    normalized === 'yes' ||
    normalized === 'true' ||
    normalized === '✓' ||
    normalized === '✔' ||
    normalized === 'x'
  ) {
    return 'x'
  }

  // Handle various "traces" formats
  if (normalized === 'traces' || normalized === 'trace') {
    return 'Traces'
  }

  // Handle "no" and empty formats
  if (
    normalized === 'no' ||
    normalized === 'false' ||
    normalized === '' ||
    normalized === "'" ||
    normalized === '.'
  ) {
    return ''
  }

  // Default to empty for unknown values
  return ''
}

// Map allergen key to display name for matching
function mapAllergenKeyToName(allergenKey) {
  const key = allergenKey.toLowerCase()
  const mapping = {
    celery: 'Celery',
    gluten: 'Gluten',
    crustaceans: 'Crustaceans',
    eggs: 'Eggs',
    fish: 'Fish',
    lupin: 'Lupin',
    milk: 'Milk',
    molluscs: 'Molluscs',
    mustard: 'Mustard',
    nuts: 'Nuts',
    peanuts: 'Peanuts',
    sesame_seeds: 'Sesame seeds',
    soya: 'Soya',
    sulphites: 'Sulphites',
  }
  return mapping[key] || allergenKey
}

// 4. ALLERGEN DATA (✓ and Traces) - Format allergen for a specific dish
function formatAllergenForDish(dish, allergenKey) {
  const allergenName = mapAllergenKeyToName(allergenKey)

  // Check if allergen is in the allergens array (contains) - ✓
  if (Array.isArray(dish.allergens) && dish.allergens.includes(allergenName)) {
    return 'yes'
  }

  // Check if allergen is in the allergenTraces array (traces) - Traces
  if (
    Array.isArray(dish.allergenTraces) &&
    dish.allergenTraces.includes(allergenName)
  ) {
    return 'traces'
  }

  // Check legacy venueProductsAllergens structure
  if (dish.venueProductsAllergens) {
    if (
      Array.isArray(dish.venueProductsAllergens.allergens) &&
      dish.venueProductsAllergens.allergens.includes(allergenName)
    ) {
      return 'yes'
    }
    if (
      Array.isArray(dish.venueProductsAllergens.allergenTraces) &&
      dish.venueProductsAllergens.allergenTraces.includes(allergenName)
    ) {
      return 'traces'
    }
  }

  // Check unified allergens object if it exists
  if (dish.allergens && typeof dish.allergens === 'object') {
    const value = dish.allergens[allergenKey] || dish.allergens[allergenName]
    if (value === 'yes' || value === 'contains' || value === true) {
      return 'yes'
    }
    if (value === 'traces' || value === 'trace') {
      return 'traces'
    }
  }

  return 'no' // No allergen present - will be normalized to blank
}
